import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '@repo/ui';
import { format } from 'date-fns';

export interface TimeSeriesData {
  date: string;
  [key: string]: string | number;
}

interface DataSeries {
  key: string;
  name: string;
  color: string;
  formatter?: (value: number | undefined) => string;
}

interface TimeSeriesChartProps {
  title: string;
  data: TimeSeriesData[];
  series: DataSeries[];
  yAxisLabel?: string;
}

export function TimeSeriesChart({ title, data, series, yAxisLabel }: TimeSeriesChartProps) {
  // Sort data by date (oldest first)
  const sortedData = [...data].reverse();
  
  // Format data for Recharts
  const chartData = sortedData.map(d => ({
    date: format(new Date(d.date), 'MM/dd'),
    ...series.reduce((acc, s) => {
      acc[s.key] = d[s.key];
      return acc;
    }, {} as Record<string, any>),
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="date"
              interval="preserveStartEnd"
              angle={-45}
              textAnchor="end"
              height={60}
              stroke="#6b7280"
            />
            <YAxis 
              stroke="#6b7280"
              label={yAxisLabel ? { 
                value: yAxisLabel, 
                angle: -90, 
                position: 'insideLeft',
                style: { fill: '#6b7280' }
              } : undefined}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#1f2937', 
                border: 'none', 
                borderRadius: '8px',
                color: '#fff'
              }}
              formatter={(value: number | undefined, name: string | undefined) => {
                if (value === undefined) return ['N/A', name || 'Unknown'];
                const seriesItem = series.find(s => s.key === name);
                const formatted = seriesItem?.formatter 
                  ? seriesItem.formatter(value)
                  : value.toLocaleString();
                return [formatted, seriesItem?.name || name || 'Unknown'];
              }}
              labelFormatter={(label) => `Date: ${label}`}
            />
            <Legend />
            {series.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                stroke={s.color}
                strokeWidth={2}
                dot={{ r: 2 }}
                connectNulls={true}
                name={s.name}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
